// ForgotPassword component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Link,
} from '@mui/material';
import { LockResetOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!formData.email.trim() && !formData.username.trim()) {
      setError(t('auth.forgotPassword.emailOrUsernameRequired', 'Please enter either email or username'));
      return;
    }

    setLoading(true);
    try {
      await apiService.post('/api/auth/forgot-password', {
        email: formData.email.trim() || undefined,
        username: formData.username.trim() || undefined,
      });

      setSuccess(true);
    } catch (err: any) {
      console.error('Failed to request password reset:', err);
      // API always returns success, but handle errors just in case
      const errorMessage = err.response?.data?.detail || err.detail || err.message || 
        t('auth.forgotPassword.error', 'Failed to request password reset. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'success.main' }}>
                  <LockResetOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.forgotPassword.checkEmail', 'Check Your Email')}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t(
                  'auth.forgotPassword.successMessage',
                  'If the email/username exists, a password reset link has been sent. Please check your email and follow the instructions.'
                )}
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                {t('auth.backToLogin', 'Back to Login')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <LockResetOutlined />
              </Avatar>
              <Typography component="h1" variant="h5">
                {t('auth.forgotPassword.title', 'Forgot Password')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {t(
                  'auth.forgotPassword.subtitle',
                  'Enter your email or username and we\'ll send you a link to reset your password.'
                )}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label={t('auth.email', 'Email')}
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
                helperText={t('auth.forgotPassword.emailHelper', 'Or enter your username below')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', my: 1 }}>
                {t('common.or', 'OR')}
              </Typography>
              <TextField
                margin="normal"
                fullWidth
                id="username"
                label={t('auth.username', 'Username')}
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleInputChange}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || (!formData.email.trim() && !formData.username.trim())}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('auth.forgotPassword.sendResetLink', 'Send Reset Link')
                )}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('auth.backToLogin', 'Back to Login')}
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForgotPassword;






